import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import DOMPurify from "dompurify";
import authStrings from "../../i18n/cs/auth.json";
import {
  CONSENT_TYPES,
  type ConsentAcceptPayload,
  type ConsentType,
  validateAcceptPayload,
} from "../../schemas/consent";
import { DiffViewer } from "./DiffViewer";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Progress } from "@/components/application/progress-steps/progress-steps";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";

/**
 * ConsentStepper — sdílená 4-step Wizard pro UC-004/005/006/009 + DEC-013.
 *
 * Kroky:
 *   1. Read         — zobrazí markdown text aktuální verze (sanitizovaný DOMPurify).
 *   2. Acknowledge  — checkbox "potvrzuji, že jsem si přečetl"; bez něj NEXT disabled.
 *   3. Accept       — review summary + tlačítko "Souhlasím se zpracováním".
 *   4. Success      — confirmation screen + redirect callback.
 *
 * Vzhled jede na Untitled UI (Progress / Button / InlineAlert) — žádné legacy
 * `consent-stepper__*` CSS. Touch targety + focus visible řeší samy komponenty.
 *
 * DOMPurify: text consent může být markdown s HTML; pre-render se vyčistí
 * — povolené tagy: p, ul, ol, li, strong, em, a, h1-h4 (REQ-SEC-016).
 */

const STEPS = ["read", "acknowledge", "accept", "success"] as const;
type StepName = (typeof STEPS)[number];

const STEP_DESCRIPTIONS: Record<StepName, string> = {
  read: "Podmínky",
  acknowledge: "Potvrzení",
  accept: "Souhlas",
  success: "Hotovo",
};

type StepStatus = "incomplete" | "current" | "complete";

export interface ConsentStepperProps {
  /** Latest material verze (e.g. "2026-05-13"). */
  consentTextVersion: string;
  /** Markdown / HTML obsah textu k zobrazení (admin-provided). */
  consentTextHtml: string;
  /** Volitelně předchozí verze pro DiffViewer (přeskočí se, pokud null). */
  previousVersion?: string;
  /** Volitelně předchozí text pro DiffViewer. */
  previousVersionHtml?: string;
  /** Které consent_types se prezentují (default: data_processing + data_sharing_axigon). */
  consentTypes?: ConsentType[];
  /** Callback zavolán po úspěšném accept (POST /api/tenant/consent/accept). */
  onAccept: (payload: ConsentAcceptPayload) => Promise<void> | void;
  /** Callback pro Cancel button (logout flow). */
  onCancel?: () => void;
}

export function ConsentStepper({
  consentTextVersion,
  consentTextHtml,
  previousVersion,
  previousVersionHtml,
  consentTypes,
  onAccept,
  onCancel,
}: ConsentStepperProps): ReactElement {
  const [step, setStep] = useState<StepName>("read");
  const [acknowledged, setAcknowledged] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stepIdx = STEPS.indexOf(step);
  const totalSteps = STEPS.length;

  const requestedTypes = useMemo<ConsentType[]>(() => {
    if (consentTypes && consentTypes.length > 0) return consentTypes;
    return ["data_processing", "data_sharing_axigon"];
  }, [consentTypes]);

  const sanitizedHtml = useMemo(() => sanitize(consentTextHtml), [consentTextHtml]);

  const stepRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Move focus na primární H2 nadpis kroku při změně kroku.
    if (stepRef.current) {
      const heading = stepRef.current.querySelector("h2");
      heading?.focus();
    }
  }, [step]);

  const goNext = useCallback(() => {
    const idx = STEPS.indexOf(step);
    if (idx < totalSteps - 1) {
      setStep(STEPS[idx + 1]!);
      setError(null);
    }
  }, [step, totalSteps]);

  const goBack = useCallback(() => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) {
      setStep(STEPS[idx - 1]!);
      setError(null);
    }
  }, [step]);

  const handleAccept = useCallback(async () => {
    setError(null);
    const payload = {
      consent_text_version: consentTextVersion,
      consent_types: requestedTypes,
    };
    const validation = validateAcceptPayload(payload);
    if (!validation.ok || !validation.value) {
      setError(validation.error ?? authStrings.consent.error.validation);
      return;
    }
    setSubmitting(true);
    try {
      await onAccept(validation.value);
      setStep("success");
    } catch (err) {
      setError(messageFromError(err));
    } finally {
      setSubmitting(false);
    }
  }, [consentTextVersion, onAccept, requestedTypes]);

  return (
    <div
      role="region"
      aria-label={authStrings.consent.stepper.title}
      className="flex flex-col gap-5 rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary"
    >
      <div className="flex flex-col gap-3">
        <h2 className="m-0 text-md font-semibold text-primary">
          {authStrings.consent.stepper.title}
        </h2>
        <Progress.IconsWithText
          type="number"
          orientation="horizontal"
          size="sm"
          items={STEPS.map((s, idx) => ({
            title: authStrings.consent.stepper.steps[s],
            description: STEP_DESCRIPTIONS[s],
            status: (idx < stepIdx
              ? "complete"
              : idx === stepIdx
                ? "current"
                : "incomplete") as StepStatus,
          }))}
        />
      </div>

      <div className="min-h-[12rem]" ref={stepRef}>
        {step === "read" && (
          <ReadStep
            consentTextVersion={consentTextVersion}
            sanitizedHtml={sanitizedHtml}
            previousVersion={previousVersion}
            previousVersionHtml={previousVersionHtml}
            showDiff={showDiff}
            onToggleDiff={() => setShowDiff((v) => !v)}
          />
        )}
        {step === "acknowledge" && (
          <AcknowledgeStep acknowledged={acknowledged} onChange={setAcknowledged} />
        )}
        {step === "accept" && (
          <AcceptStep
            consentTextVersion={consentTextVersion}
            requestedTypes={requestedTypes}
            error={error}
          />
        )}
        {step === "success" && <SuccessStep />}
      </div>

      <div
        className="flex flex-wrap items-center gap-3 border-t border-secondary pt-4"
        role="group"
        aria-label="Akce"
      >
        {step !== "success" && onCancel && (
          <Button color="secondary" className="mr-auto" onPress={onCancel} isDisabled={submitting}>
            {authStrings.consent.stepper.cancel}
          </Button>
        )}
        {step !== "read" && step !== "success" && (
          <Button color="secondary" onPress={goBack} isDisabled={submitting}>
            {authStrings.consent.stepper.back}
          </Button>
        )}
        {step === "read" && (
          <Button color="primary" onPress={goNext}>
            {authStrings.consent.stepper.next}
          </Button>
        )}
        {step === "acknowledge" && (
          <Button color="primary" onPress={goNext} isDisabled={!acknowledged}>
            {authStrings.consent.stepper.next}
          </Button>
        )}
        {step === "accept" && (
          <Button color="primary" onPress={handleAccept} isLoading={submitting}>
            {authStrings.consent.stepper.accept}
          </Button>
        )}
      </div>
    </div>
  );
}

const CONSENT_TEXT_CLASSES =
  "max-h-[400px] overflow-y-auto rounded-xl bg-secondary p-4 text-sm leading-relaxed text-primary ring-1 ring-secondary " +
  "[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-0 [&_h1]:mb-2 " +
  "[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 " +
  "[&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 " +
  "[&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 " +
  "[&_a]:text-brand-secondary [&_a]:underline";

interface ReadStepProps {
  consentTextVersion: string;
  sanitizedHtml: string;
  previousVersion?: string;
  previousVersionHtml?: string;
  showDiff: boolean;
  onToggleDiff: () => void;
}

function ReadStep({
  consentTextVersion,
  sanitizedHtml,
  previousVersion,
  previousVersionHtml,
  showDiff,
  onToggleDiff,
}: ReadStepProps): ReactElement {
  const hasDiff = previousVersion !== undefined && previousVersionHtml !== undefined;
  return (
    <section aria-labelledby="consent-read-heading" className="flex flex-col gap-3">
      <h2
        id="consent-read-heading"
        tabIndex={-1}
        className="text-lg font-semibold text-primary m-0 outline-none"
      >
        Verze {consentTextVersion}
      </h2>
      <p className="text-sm text-tertiary m-0">{authStrings.consent.stepper.intro}</p>
      <article
        className={CONSENT_TEXT_CLASSES}
        // sanitizedHtml is already passed through DOMPurify.sanitize().
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
      {hasDiff && (
        <div className="flex flex-col gap-2">
          <Button color="link-color" size="sm" onPress={onToggleDiff} aria-expanded={showDiff}>
            {showDiff ? authStrings.consent.diff.hideDiff : authStrings.consent.diff.showDiff}
          </Button>
          {showDiff && previousVersionHtml !== undefined && (
            <DiffViewer
              previous={stripHtml(previousVersionHtml)}
              current={stripHtml(sanitizedHtml)}
              label={authStrings.consent.diff.title}
            />
          )}
        </div>
      )}
    </section>
  );
}

interface AcknowledgeStepProps {
  acknowledged: boolean;
  onChange: (v: boolean) => void;
}

function AcknowledgeStep({ acknowledged, onChange }: AcknowledgeStepProps): ReactElement {
  return (
    <section aria-labelledby="consent-ack-heading" className="flex flex-col gap-3">
      <h2
        id="consent-ack-heading"
        tabIndex={-1}
        className="text-lg font-semibold text-primary m-0 outline-none"
      >
        {authStrings.consent.stepper.steps.acknowledge}
      </h2>
      <div className="p-4 rounded-xl bg-secondary ring-1 ring-secondary">
        <Checkbox
          isSelected={acknowledged}
          onChange={onChange}
          label={authStrings.consent.acknowledge.label}
        />
      </div>
      <p id="consent-ack-warning" className="text-sm text-tertiary m-0">
        {authStrings.consent.acknowledge.warning}
      </p>
    </section>
  );
}

interface AcceptStepProps {
  consentTextVersion: string;
  requestedTypes: ConsentType[];
  error: string | null;
}

function AcceptStep({ consentTextVersion, requestedTypes, error }: AcceptStepProps): ReactElement {
  return (
    <section aria-labelledby="consent-accept-heading" className="flex flex-col gap-4">
      <h2
        id="consent-accept-heading"
        tabIndex={-1}
        className="text-lg font-semibold text-primary m-0 outline-none"
      >
        {authStrings.consent.stepper.steps.accept}
      </h2>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm m-0">
        <dt className="font-medium text-tertiary">Verze textu</dt>
        <dd className="text-primary m-0">{consentTextVersion}</dd>
        <dt className="font-medium text-tertiary">Souhlasy k udělení</dt>
        <dd className="text-primary m-0">
          <ul className="list-disc pl-5 space-y-1">
            {requestedTypes.map((t) => (
              <li key={t}>{authStrings.consent.type[t]}</li>
            ))}
          </ul>
        </dd>
      </dl>
      {error && (
        <InlineAlert variant="error" title={authStrings.consent.error.title}>
          {error}
        </InlineAlert>
      )}
    </section>
  );
}

function SuccessStep(): ReactElement {
  return (
    <section aria-labelledby="consent-success-heading" className="flex flex-col gap-2">
      <h2
        id="consent-success-heading"
        tabIndex={-1}
        className="text-lg font-semibold text-primary m-0 outline-none"
      >
        {authStrings.consent.stepper.successTitle}
      </h2>
      <p className="text-tertiary m-0">{authStrings.consent.stepper.successDescription}</p>
    </section>
  );
}

/**
 * Sanitize markdown/HTML pro admin-published consent text. Allowed:
 *  - block: p, ul, ol, li, h1, h2, h3, h4, blockquote
 *  - inline: strong, em, a (s href + target=_blank + rel=noopener)
 *  - žádné iframe, script, style, object, embed, etc.
 */
export function sanitize(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "blockquote",
      "strong",
      "em",
      "a",
      "br",
      "code",
    ],
    ALLOWED_ATTR: ["href", "target", "rel"],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#)/i,
  });
}

function stripHtml(html: string): string {
  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent ?? "";
  }
  return html.replace(/<[^>]*>/g, "");
}

function messageFromError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return authStrings.consent.error.network;
}

// Re-export pro test/external use.
export { CONSENT_TYPES };
